import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams, Link } from 'react-router-dom';
import './App.css';
import LOGO_SRC from './logo';
import { NUTRAZEN_LOGO, BS_TRENDZ_LOGO, SMCO_LOGO, DUMSAMKER_LOGO } from './brandLogos';

const BRAND_LOGOS = {
  nutrazen:   NUTRAZEN_LOGO,
  'bs-trendz': BS_TRENDZ_LOGO,
  smco:        SMCO_LOGO,
  dumsamker:   DUMSAMKER_LOGO,
};

// ================================================================
// SEO UTILITY — sets title & meta description per page
// ================================================================
const useSEO = (title, description) => {
  useEffect(() => {
    document.title = title;
    let meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', description);
  }, [title, description]);
};

// ================================================================
// BRAND DATA — placeholder content for all 4 brands
// ================================================================
const BRANDS_DATA = {
  nutrazen: {
    slug: 'nutrazen',
    name: 'NutraZen',
    indicator: 'N',
    category: 'Supplements & Wellness',
    tagline: 'From a single listing to an award-winning wellness brand.',
    colour: '#affd86',
    siteUrl: 'https://nutrazen.co.uk/',
    intro: `NutraZen came to FastTrack Wholesale as a promising but underperforming supplements brand with a small product footprint and no structured ecommerce strategy. The products had genuine quality behind them, but the distribution and presentation simply hadn't caught up. We saw the potential immediately.`,
    beforeTitle: 'When NutraZen Arrived',
    beforePoints: [
      'Single ASIN live on Amazon UK with no catalogue depth',
      'Limited A+ content with no brand store or brand story',
      'Limited keyword coverage and poor search visibility',
      'Averaging £3,000 per month in revenue',
      'No scope for Amazon EU marketplaces',
      'No FBA optimisation with recurring out-of-stock periods',
    ],
    afterTitle: 'Where NutraZen Stands Today',
    afterPoints: [
      'Product catalogue developed with multiple products in the pipeline for 2026',
      'A+ content and brand storefront live on Amazon UK',
      'Keyword-optimised listings driving consistent organic rank',
      'Averaging £20,000 per month in revenue across regions',
      'Launching across Amazon PAN EU marketplaces',
      'Achieved Best Seller, Amazon Choice and Rank 1 BSR for Ginger Herbal Supplements',
    ],
    metrics: [
      { value: '+566%', label: 'Year on Year Revenue Growth' },
      { value: '2', label: 'Active ASINs' },
      { value: 'Rank 1', label: 'Ginger Herbal Supplements BSR' },
      { value: 'Best Seller', label: "Amazon's Choice & Best Seller" },
    ],
    whatWeDid: [
      'Conducted a full catalogue and competitor audit on arrival',
      'Rebuilt all product listings with professional copy and keyword research',
      'Created premium A+ content and brand storefront to increase conversion rate',
      'Developed an inventory forecasting model to eliminate stockouts',
      'Launched NutraZen on Amazon PAN EU fulfilment. Go live is TBC',
      'Implemented a structured PPC advertising strategy across all markets',
      'Introduced a seasonal promotional calendar to capitalise on demand peaks',
    ],
  },

  'bs-trendz': {
    slug: 'bs-trendz',
    name: 'B&S Trendz',
    indicator: 'B',
    category: 'Party Wear & Novelty',
    tagline: 'Turning seasonal spikes into year-round commercial strength.',
    colour: '#affd86',
    intro: `B&S Trendz arrived as a classic seasonal business. Strong Q4 numbers were driven by party and fancy dress demand, but there was little trading activity throughout the rest of the year. The range was good. The catalogue management and distribution strategy needed a complete restructure.`,
    beforeTitle: 'When B&S Trendz Arrived',
    beforePoints: [
      '5 active ASINs, predominantly seasonal product lines',
      'Almost entirely Q4-dependent with minimal January to September revenue',
      'UK marketplace only with no EU distribution',
      'Averaging £6,500 per month across the year',
      'No year-round product strategy or off-season planning',
      'Stock management issues leading to Q4 shortfalls',
      'Limited PPC activity and no structured advertising calendar',
    ],
    afterTitle: 'Where B&S Trendz Stands Today',
    afterPoints: [
      '9 active ASINs with a balanced seasonal and evergreen mix',
      'Consistent 12-month trading pattern established',
      'Peak Q4 revenue now reaching £24,000 per month',
      'Year-round monthly average exceeding £13,650',
      'EU marketplace launch in progress for select product lines',
      'Structured seasonal forecasting eliminating Q4 stock shortfalls',
      'Bestseller badge achieved in Fancy Dress and Inflatable Props category during Q4',
    ],
    metrics: [
      { value: '+110%', label: 'Peak Revenue Growth' },
      { value: '9', label: 'Active ASINs' },
      { value: '9 Years', label: 'Active Trading' },
      { value: 'Multi #1', label: 'Best Sellers & Amazon Choice' },
    ],
    whatWeDid: [
      'Rebuilt the product catalogue to include evergreen novelty and party lines',
      'Developed a 12-month promotional and inventory calendar',
      'Introduced year-round PPC campaigns across relevant subcategories',
      'Expanded into Halloween, Christmas, Easter and Summer event-led product lines',
      'Restructured pricing to remain competitive across all seasons',
      'Implemented Q4 inventory pre-ordering 16 weeks ahead of peak demand',
      'Built a brand storefront to cross-sell across the full range',
    ],
  },

  smco: {
    slug: 'smco',
    name: 'SMCO',
    indicator: 'S',
    category: 'Office & Home',
    tagline: 'Brand Registry secured. Catalogue expanded. Revenue transformed.',
    colour: '#affd86',
    intro: `SMCO is a practical, everyday brand covering home organisation and office essentials. When it arrived, the fundamentals were there but the brand had no formal Amazon Brand Registry, listing quality was inconsistent and the catalogue was underdeveloped. The opportunity was clear, and the execution was straightforward once the foundations were properly set.`,
    beforeTitle: 'When SMCO Arrived',
    beforePoints: [
      '2 active ASINs with inconsistent listing quality across the range',
      'No Amazon Brand Registry, leaving listings vulnerable to hijackers',
      'No A+ content, no brand storefront, limited visual presentation',
      'Averaging £4,000 per month in revenue',
      'UK marketplace only with no international distribution',
      'No structured advertising, relying solely on organic search',
      'Catalogue gaps in core subcategories with missed revenue potential',
    ],
    afterTitle: 'Where SMCO Stands Today',
    afterPoints: [
      '10+ active ASINs covering the full home and office essentials range',
      'Amazon Brand Registry secured, with listings now protected and controlled',
      'A+ content live across all primary ASINs with consistent brand presentation',
      'Averaging £8,300 per month in revenue',
      'Launched across Amazon UK and EU (DE, FR, IT)',
      'Structured PPC delivering positive ROAS across all active markets',
      'Catalogue now covers all identified subcategory opportunities',
    ],
    metrics: [
      { value: '+30%', label: 'Year on Year Revenue Growth' },
      { value: '10+', label: 'Active ASINs' },
      { value: 'UK & EU', label: 'Active Markets' },
      { value: 'Secured', label: 'Brand Registry' },
    ],
    whatWeDid: [
      'Initiated and completed the Amazon Brand Registry application process',
      'Audited all existing ASINs and rebuilt listings with professional copy',
      'Identified catalogue gaps through category and competitor research',
      'Created A+ content and brand storefront reflecting a consistent identity',
      'Launched advertising campaigns across all active product lines',
      'Expanded distribution to Amazon DE, FR and IT marketplaces',
    ],
  },

  dumsamker: {
    slug: 'dumsamker',
    name: 'DUMSAMKER',
    indicator: 'D',
    category: 'Accessibility & Practical Aids',
    tagline: 'A specialist niche, unlocked and structured for growth.',
    colour: '#affd86',
    intro: `DUMSAMKER operates in a genuinely underserved area of ecommerce, covering practical accessibility aids and adaptive daily living products. The products address real consumer needs, but the brand had barely scratched the surface of its market opportunity. FastTrack identified the structural potential and built a proper distribution and catalogue strategy around it.`,
    beforeTitle: 'When DUMSAMKER Arrived',
    beforePoints: [
      '4 active ASINs representing a very limited initial product footprint',
      'Largely undiscovered on Amazon with minimal search visibility',
      'Inconsistent pricing with no competitive analysis in place',
      'Averaging £900 per month in revenue',
      'No advertising activity with zero PPC investment',
      'No brand presence or storefront, leaving the brand invisible to repeat buyers',
      'No structured plan for catalogue or market expansion',
    ],
    afterTitle: 'Where DUMSAMKER Stands Today',
    afterPoints: [
      '4 active ASINs across core reacher grabber tool niche',
      'Established keyword rankings in targeted accessibility search terms',
      'Structured pricing strategy delivering consistent margins',
      'Potential earnings averaging £25,000 per month',
      'Active PPC campaigns running across all product lines',
      'Brand storefront live with clear category navigation',
      'Identified further catalogue expansion opportunities across 5 subcategories',
    ],
    metrics: [
      { value: '2,677%', label: 'Potential Growth This Year' },
      { value: '4', label: 'Active ASINs' },
      { value: '10,000+', label: 'Reviews' },
      { value: 'UK', label: 'Primary Market' },
    ],
    whatWeDid: [
      'Conducted in-depth category research across accessibility and mobility aids',
      'Identified the highest-opportunity subcategories for catalogue development',
      'Built keyword strategy around accessibility and adaptive product search terms',
      'Launched structured PPC campaigns with conservative initial budgets',
      'Created brand storefront to build recognition with repeat purchase customers',
      'Established a pricing strategy benchmarked against specialist category competitors',
    ],
  },
};

// ================================================================
// ICONS
// ================================================================
const HomeIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);
const LeafIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 8C8 10 5.9 16.17 3.82 19.11a1 1 0 0 0 .16 1.31l.46.45a1 1 0 0 0 1.4-.1C7.1 19.04 9 17 12 17c5 0 9-4 9-9 0-2.21-.895-4.21-2.343-5.657"/>
    <line x1="7" y1="17" x2="17" y2="7"/>
  </svg>
);
const PillIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.5 20.5L3.5 13.5a5 5 0 0 1 7.07-7.07l7 7a5 5 0 0 1-7.07 7.07z"/>
    <line x1="8.5" y1="11.5" x2="15.5" y2="4.5"/>
  </svg>
);
const HeartIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
);
const PawIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="4" r="2"/><circle cx="18" cy="8" r="2"/><circle cx="4" cy="8" r="2"/>
    <circle cx="6.5" cy="15.5" r="2"/>
    <path d="M17.5 15.5L12 21.5 6.5 15.5c-1-1.5 0-3.5 2-3.5h7c2 0 3 2 2 3.5z"/>
  </svg>
);
const GameIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <line x1="6" y1="12" x2="10" y2="12"/><line x1="8" y1="10" x2="8" y2="14"/>
    <line x1="15" y1="13" x2="15.01" y2="13" strokeWidth="2"/>
    <line x1="18" y1="11" x2="18.01" y2="11" strokeWidth="2"/>
    <rect x="2" y="6" width="20" height="12" rx="2"/>
  </svg>
);
const StarIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);
const FoodIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 8h1a4 4 0 1 1 0 8h-1"/>
    <path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V8z"/>
    <line x1="6" y1="2" x2="6" y2="4"/><line x1="10" y1="2" x2="10" y2="4"/><line x1="14" y1="2" x2="14" y2="4"/>
  </svg>
);
const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const ArrowRightIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);
const ArrowLeftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
  </svg>
);
const MenuIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);
const CloseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const CameraIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="14" rx="2"/>
    <circle cx="12" cy="12" r="3"/>
    <path d="M8.5 5l1.5-2h4l1.5 2"/>
  </svg>
);

// ================================================================
// SHARED COMPONENTS
// ================================================================
const SectionLabel = ({ children, light = false }) => (
  <div className={`ftw-section-label ${light ? 'ftw-section-label--light' : ''}`}>
    {children}
  </div>
);

const PhotoPlaceholder = ({ aspectRatio = '4/3', label = 'Photo', className = '' }) => (
  <div className={`ftw-photo-placeholder ${className}`} style={{ aspectRatio }}>
    <div className="ftw-photo-placeholder__inner">
      <CameraIcon />
      <span>{label}</span>
    </div>
  </div>
);

// ================================================================
// NAVIGATION
// ================================================================
const Navigation = ({ showBack = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    setMobileOpen(false);
    if (showBack) {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav className={`ftw-nav ${scrolled ? 'ftw-nav--scrolled' : ''}`}>
      <div className="ftw-nav__inner container">
        <img
          src={LOGO_SRC}
          alt="FastTrack Wholesale"
          className="ftw-nav__logo-img"
          onClick={() => navigate('/')}
        />
        <div className="ftw-nav__links">
          <button className="ftw-nav__link" onClick={() => scrollTo('who-we-are')}>About</button>
          <button className="ftw-nav__link" onClick={() => scrollTo('sectors')}>Sectors</button>
          <button className="ftw-nav__link" onClick={() => scrollTo('brands')}>Brands</button>
          <button className="ftw-nav__link" onClick={() => scrollTo('model')}>Our Model</button>
          <button className="ftw-btn ftw-btn--primary ftw-nav__cta" onClick={() => scrollTo('cta')}>Discuss Partnership</button>
        </div>
        <button className="ftw-nav__hamburger" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
          {mobileOpen ? <CloseIcon /> : <MenuIcon />}
        </button>
      </div>
      {mobileOpen && (
        <div className="ftw-nav__mobile">
          <button className="ftw-nav__mobile-link" onClick={() => scrollTo('who-we-are')}>About</button>
          <button className="ftw-nav__mobile-link" onClick={() => scrollTo('sectors')}>Sectors</button>
          <button className="ftw-nav__mobile-link" onClick={() => scrollTo('brands')}>Brands</button>
          <button className="ftw-nav__mobile-link" onClick={() => scrollTo('model')}>Our Model</button>
          <button className="ftw-btn ftw-btn--amber ftw-nav__mobile-cta" onClick={() => scrollTo('cta')}>Discuss Partnership</button>
        </div>
      )}
    </nav>
  );
};

// ================================================================
// FOOTER
// ================================================================
const Footer = () => {
  const navigate = useNavigate();
  const scrollTo = (id) => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <footer className="ftw-footer">
      <div className="container ftw-footer__inner">
        <div className="ftw-footer__brand">
          <img
            src={LOGO_SRC}
            alt="FastTrack Wholesale"
            className="ftw-footer__logo-img"
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer' }}
          />
          <p className="ftw-footer__tagline">Established wholesale &amp; ecommerce distribution across UK, US &amp; European markets.</p>
          <address className="ftw-footer__address">
            Unit 12 Heybridge House Industrial Estate<br />
            Maldon, Essex, CM9 4XL
          </address>
          <div className="ftw-footer__platforms">
            <span className="ftw-footer__platform-badge">Amazon UK</span>
            <span className="ftw-footer__platform-badge">Amazon US</span>
            <span className="ftw-footer__platform-badge">Amazon EU</span>
          </div>
        </div>
        <div className="ftw-footer__col">
          <h5 className="ftw-footer__col-title">Company</h5>
          <span className="ftw-footer__link" onClick={() => scrollTo('who-we-are')}>About Us</span>
          <span className="ftw-footer__link" onClick={() => scrollTo('brands')}>Our Brands</span>
          <span className="ftw-footer__link" onClick={() => scrollTo('model')}>Wholesale Model</span>
          <span className="ftw-footer__link" onClick={() => scrollTo('expansion')}>Expansion</span>
        </div>
        <div className="ftw-footer__col">
          <h5 className="ftw-footer__col-title">Sectors</h5>
          {['Home & Kitchen', 'Garden & Outdoors', 'Healthcare', 'Pharmaceutical', 'Pet', 'Games & Hobbies'].map((s, i) => (
            <span key={i} className="ftw-footer__sector">{s}</span>
          ))}
        </div>
        <div className="ftw-footer__col">
          <h5 className="ftw-footer__col-title">Contact</h5>
          <p className="ftw-footer__contact-note">For wholesale partnership enquiries:</p>
          <a href="mailto:partnerships@fasttrackwholesale.co.uk" className="ftw-footer__email">
            partnerships@<br />fasttrackwholesale.co.uk
          </a>
          <button className="ftw-btn ftw-btn--amber ftw-footer__cta" onClick={() => scrollTo('cta')}>
            Discuss Partnership
          </button>
        </div>
      </div>
      <div className="ftw-footer__bottom">
        <div className="container ftw-footer__bottom-inner">
          <span>&copy; {new Date().getFullYear()} FastTrack Wholesale. All rights reserved.</span>
          <span>Wholesale &amp; Ecommerce Distribution, UK, US &amp; Europe</span>
        </div>
      </div>
    </footer>
  );
};

// ================================================================
// LANDING PAGE SECTIONS
// ================================================================
const HeroSection = () => (
  <section className="ftw-hero" id="home">
    <div className="ftw-hero__grid-overlay" />
    <div className="ftw-hero__circle-motif" />
    <div className="container ftw-hero__inner">
      <div className="ftw-hero__content">
        <div className="ftw-hero__label ftw-hero-animate ftw-hero-animate--1">Wholesale &amp; Ecommerce Distribution</div>
        <h1 className="ftw-hero__headline ftw-hero-animate ftw-hero-animate--2">
          Established Wholesale &amp; Ecommerce Distribution Partner
        </h1>
        <p className="ftw-hero__subtext ftw-hero-animate ftw-hero-animate--3">
          FastTrack Wholesale partners with recognised and emerging brands to distribute products across Amazon and major ecommerce platforms throughout the UK, US and Europe.
        </p>
        <div className="ftw-hero__actions ftw-hero-animate ftw-hero-animate--4">
          <button className="ftw-btn ftw-btn--amber ftw-btn--lg" onClick={() => document.getElementById('cta').scrollIntoView({ behavior: 'smooth' })}>
            Discuss a Wholesale Partnership <ArrowRightIcon />
          </button>
          <button className="ftw-btn ftw-btn--outline-light ftw-btn--lg" onClick={() => document.getElementById('brands').scrollIntoView({ behavior: 'smooth' })}>
            Explore Our Brand Portfolio
          </button>
        </div>
      </div>
      <div className="ftw-hero__aside ftw-hero-animate ftw-hero-animate--5">
        <div className="ftw-hero__metric-card">
          <div className="ftw-hero__metric-rule" />
          <div className="ftw-hero__markets">
            <span className="ftw-hero__market-label">Active Markets</span>
            <div className="ftw-hero__market-tags">
              <span className="ftw-hero__market-tag">United Kingdom</span>
              <span className="ftw-hero__market-tag">United States</span>
              <span className="ftw-hero__market-tag">Europe</span>
            </div>
          </div>
          <div className="ftw-hero__divider" />
          <div className="ftw-hero__features">
            {[
              { title: 'International Marketplace Coverage', text: 'Amazon UK, US and EU with expanding regional presence.' },
              { title: 'Multi-Sector Distribution', text: 'Diversified across major consumer product categories.' },
              { title: 'Brand Portfolio Operators', text: 'Simultaneously managing owned and wholesale brands.' },
              { title: 'Structured Growth Infrastructure', text: 'Inventory investment, listing optimisation and cross-market expansion.' },
            ].map((f, i) => (
              <div className="ftw-hero__feature" key={i}>
                <span className="ftw-hero__feature-title">{f.title}</span>
                <span className="ftw-hero__feature-text">{f.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
    <div className="ftw-hero__scroll-indicator ftw-hero-animate ftw-hero-animate--6">
      <div className="ftw-hero__scroll-line" />
      <span>scroll</span>
    </div>
  </section>
);

const WhoWeAreSection = () => (
  <section className="ftw-section ftw-section--light" id="who-we-are">
    <div className="container">
      <div className="ftw-who__inner">
        <div className="ftw-who__left ftw-animate-reveal">
          <SectionLabel>Who We Are</SectionLabel>
          <h2 className="ftw-section-heading">A Structured Wholesale &amp; Distribution Operation</h2>
          <p className="ftw-section-lead">
            FastTrack Wholesale operates as a direct wholesale and ecommerce distribution partner, purchasing inventory outright, managing marketplace presence and building long-term brand relationships across multiple consumer categories.
          </p>
          <PhotoPlaceholder aspectRatio="4/3" label="Who We Are" className="ftw-who__photo" />
        </div>
        <div className="ftw-who__right">
          {[
            { num: '01', title: 'Direct Inventory Purchasing', text: 'We purchase stock directly, providing brands with immediate wholesale revenue and removing fulfilment complexity.' },
            { num: '02', title: 'Authorised Ecommerce Distribution', text: 'Operating as an authorised distributor, ensuring brand integrity is maintained across all marketplace channels.' },
            { num: '03', title: 'Long-Term Wholesale Relationships', text: 'We build structured, ongoing wholesale agreements focused on sustainable, consistent growth rather than one-off transactions.' },
            { num: '04', title: 'Structured Marketplace Execution', text: 'Disciplined listing management, advertising strategy and inventory planning ensure products perform at their potential.' },
          ].map((point, i) => (
            <div className="ftw-who__point ftw-animate-reveal" key={i} style={{ animationDelay: `${i * 80}ms` }}>
              <span className="ftw-who__point-num">{point.num}</span>
              <div>
                <h4 className="ftw-who__point-title">{point.title}</h4>
                <p className="ftw-who__point-text">{point.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const SectorsSection = () => {
  const sectors = [
    { icon: <HomeIcon />, label: 'Home & Kitchen' },
    { icon: <LeafIcon />, label: 'Garden & Outdoors' },
    { icon: <PillIcon />, label: 'Pharmaceutical' },
    { icon: <HeartIcon />, label: 'Healthcare' },
    { icon: <PawIcon />, label: 'Pet' },
    { icon: <GameIcon />, label: 'Games & Hobbies' },
    { icon: <StarIcon />, label: 'Fancy Dress & Novelty' },
    { icon: <FoodIcon />, label: 'Food & Drink' },
  ];
  return (
    <section className="ftw-section ftw-section--white" id="sectors">
      <div className="container">
        <div className="ftw-sectors__header ftw-animate-reveal">
          <SectionLabel>Sectors We Operate In</SectionLabel>
          <h2 className="ftw-section-heading">Eight Consumer Categories. One Distribution Partner.</h2>
          <p className="ftw-sectors__note">We are active across these sectors and continuously expanding our brand portfolio within each.</p>
        </div>
        <div className="ftw-sectors__grid">
          {sectors.map((sector, i) => (
            <div className="ftw-sector-card ftw-animate-reveal" key={i} style={{ animationDelay: `${i * 60}ms` }}>
              <div className="ftw-sector-card__icon">{sector.icon}</div>
              <span className="ftw-sector-card__label">{sector.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const BrandsSection = () => {
  const navigate = useNavigate();
  const brands = Object.values(BRANDS_DATA);

  return (
    <section className="ftw-section ftw-section--mid" id="brands">
      <div className="container">
        <div className="ftw-brands__header ftw-animate-reveal">
          <SectionLabel light>Brands We Develop &amp; Operate</SectionLabel>
          <h2 className="ftw-section-heading ftw-section-heading--light">Building Brands from the Ground Up</h2>
          <p className="ftw-section-lead ftw-section-lead--light ftw-brands__intro">
            Beyond wholesale partnerships, FastTrack develops and operates its own brands across key consumer categories, giving us first-hand operational insight into what drives ecommerce growth.
          </p>
        </div>
        <div className="ftw-brands__photos ftw-animate-reveal">
          <PhotoPlaceholder aspectRatio="3/2" label="Product Photo" />
          <PhotoPlaceholder aspectRatio="3/2" label="Product Photo" />
          <PhotoPlaceholder aspectRatio="3/2" label="Product Photo" />
        </div>
        <div className="ftw-brands__grid">
          {brands.map((brand, i) => (
            <div
              className="ftw-brand-card ftw-brand-card--clickable ftw-animate-reveal"
              key={i}
              style={{ animationDelay: `${i * 80}ms` }}
              onClick={() => navigate(`/brands/${brand.slug}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && navigate(`/brands/${brand.slug}`)}
            >
              <div className="ftw-brand-card__top">
                <div className="ftw-brand-card__indicator">
                  <img src={BRAND_LOGOS[brand.slug]} alt={brand.name} className="ftw-brand-card__logo-img" />
                </div>
                <div>
                  <div className="ftw-brand-card__category">{brand.category}</div>
                  <h3 className="ftw-brand-card__name">{brand.name}</h3>
                </div>
              </div>
              <p className="ftw-brand-card__desc">{brand.tagline}</p>
              <div className="ftw-brand-card__footer">
                <span className="ftw-brand-card__cta">Read the story <ArrowRightIcon /></span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const WholesaleModelSection = () => {
  const points = [
    { title: 'Direct Stock Purchasing', text: 'We purchase inventory upfront at agreed wholesale prices, providing immediate revenue and removing fulfilment risk for our brand partners.' },
    { title: 'Structured Agreements', text: 'Every partnership is formalised through clear wholesale agreements that define pricing, territory, exclusivity and growth expectations.' },
    { title: 'Long-Term Growth Alignment', text: 'We build wholesale relationships for the long term, focused on consistent catalogue growth and expanding market presence rather than short-term volume.' },
  ];
  return (
    <section className="ftw-section ftw-section--dark" id="model">
      <div className="ftw-section--dark__overlay" />
      <div className="container">
        <div className="ftw-model__header ftw-animate-reveal">
          <SectionLabel light>Our Wholesale Model</SectionLabel>
          <h2 className="ftw-section-heading ftw-section-heading--light">How We Work With Brands</h2>
          <p className="ftw-section-lead ftw-section-lead--light">
            Our approach is built on clarity, commitment and commercial discipline. Every element of our wholesale model is designed to create predictable, scalable growth for our brand partners.
          </p>
        </div>
        <div className="ftw-model__grid">
          {points.map((point, i) => (
            <div className="ftw-model-card ftw-animate-reveal" key={i} style={{ animationDelay: `${i * 70}ms` }}>
              <div className="ftw-model-card__num">{String(i + 1).padStart(2, '0')}</div>
              <h4 className="ftw-model-card__title">{point.title}</h4>
              <p className="ftw-model-card__text">{point.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const MarketplaceSection = () => {
  const capabilities = [
    { label: 'Geographic Reach', text: 'Active accounts and trading history across Amazon UK, Amazon US and Amazon EU platforms, with established seller standing in each marketplace.' },
    { label: 'Inventory Planning', text: 'Systematic stock management aligned with platform lead times, seasonal demand patterns and FBA replenishment requirements to maintain healthy availability.' },
    { label: 'Compliance Awareness', text: 'Working knowledge of Amazon policies, marketplace compliance requirements and category-specific regulations across UK and international markets.' },
    { label: 'Brand Registry Collaboration', text: 'We work with brand registered suppliers to ensure listing ownership, A+ content and trademark protections are correctly applied and maintained.' },
  ];
  return (
    <section className="ftw-section ftw-section--light" id="marketplace">
      <div className="container">
        <div className="ftw-market__inner">
          <div className="ftw-market__left ftw-animate-reveal">
            <SectionLabel>Marketplace Experience</SectionLabel>
            <h2 className="ftw-section-heading">Active Across UK, US &amp; European Markets</h2>
            <p className="ftw-section-lead">
              Our marketplace experience is operational, not theoretical. We maintain active selling accounts, manage live inventory and execute across three Amazon regions every day.
            </p>
            <div className="ftw-market__regions">
              {['Amazon UK', 'Amazon US', 'Amazon EU'].map((r, i) => (
                <div className="ftw-market__region-tag" key={i}>{r}</div>
              ))}
            </div>
          </div>
          <div className="ftw-market__right">
            {capabilities.map((cap, i) => (
              <div className="ftw-market__item ftw-animate-reveal" key={i} style={{ animationDelay: `${i * 70}ms` }}>
                <div className="ftw-market__item-check"><CheckIcon /></div>
                <div>
                  <h4 className="ftw-market__item-label">{cap.label}</h4>
                  <p className="ftw-market__item-text">{cap.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const ExpansionSection = () => (
  <section className="ftw-section ftw-section--white" id="expansion">
    <div className="container">
      <div className="ftw-expansion__inner ftw-animate-reveal">
        <div className="ftw-expansion__left">
          <SectionLabel>Continuous Expansion</SectionLabel>
          <h2 className="ftw-section-heading">Always Growing. Always Seeking.</h2>
        </div>
        <div className="ftw-expansion__right">
          <p className="ftw-section-lead">
            FastTrack Wholesale is an active and expanding business. We are continuously building our brand library, welcoming new wholesale relationships and deepening our presence within every sector we operate in.
          </p>
          <div className="ftw-expansion__points">
            {[
              'Expanding brand library across all 8 operating sectors',
              'Actively seeking structured wholesale partnerships with established and emerging brands',
              'Focused on sustainable ecommerce growth, building value for brands through disciplined distribution',
              'Ongoing platform and channel diversification beyond Amazon into emerging ecommerce channels',
            ].map((point, i) => (
              <div className="ftw-expansion__point" key={i}>
                <div className="ftw-expansion__point-dot" />
                <p>{point}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const CTASection = () => (
  <section className="ftw-cta-section" id="cta">
    <div className="ftw-cta-section__bg-pattern" />
    <div className="container ftw-cta-section__inner ftw-animate-reveal">
      <div className="ftw-cta-section__content">
        <div className="ftw-cta-section__label">Get In Touch</div>
        <h2 className="ftw-cta-section__heading">Let's Build a Wholesale Relationship</h2>
        <p className="ftw-cta-section__subtext">
          If you represent a brand looking for a structured, committed wholesale distribution partner across Amazon and major ecommerce platforms, we would welcome a conversation.
        </p>
        <div className="ftw-cta-section__actions">
          <a href="mailto:partnerships@fasttrackwholesale.co.uk" className="ftw-btn ftw-btn--amber ftw-btn--xl">
            Discuss a Wholesale Partnership <ArrowRightIcon />
          </a>
          <p className="ftw-cta-section__note">All enquiries are treated with discretion and responded to promptly.</p>
        </div>
      </div>
      <div className="ftw-cta-section__aside">
        <div className="ftw-cta-aside__card">
          <h4 className="ftw-cta-aside__title">What to Include</h4>
          {[
            'Brand name and product range overview',
            'Current sales channels and volume estimates',
            'Target wholesale pricing and MOQ requirements',
            'Geographic distribution preferences',
          ].map((item, i) => (
            <div className="ftw-cta-aside__item" key={i}>
              <CheckIcon /><span>{item}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ================================================================
// LANDING PAGE
// ================================================================
const LandingPage = () => {
  useSEO(
    'FastTrack Wholesale LTD | Wholesale & Ecommerce Distribution Specialists',
    'FastTrack Wholesale LTD is a trusted wholesale and ecommerce distribution partner, delivering brand growth across Amazon and leading marketplaces throughout the UK, US and Europe.'
  );
  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.ftw-animate-reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <HeroSection />
      <WhoWeAreSection />
      <SectorsSection />
      <BrandsSection />
      <WholesaleModelSection />
      <MarketplaceSection />
      <ExpansionSection />
      <CTASection />
    </>
  );
};

// ================================================================
// BRAND DETAIL PAGE
// ================================================================
const BrandPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const brand = BRANDS_DATA[slug];

  useSEO(
    brand ? `${brand.name} | FastTrack Wholesale LTD` : 'Brand | FastTrack Wholesale LTD',
    brand ? `${brand.name} — ${brand.tagline} Distributed by FastTrack Wholesale LTD across Amazon UK, US and EU.` : ''
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.ftw-animate-reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [slug]);

  if (!brand) {
    return (
      <div className="ftw-brand-page">
        <div style={{ padding: '200px 0', textAlign: 'center' }}>
          <p style={{ color: 'white' }}>Brand not found.</p>
          <button className="ftw-btn ftw-btn--amber" onClick={() => navigate('/')}>Back to Home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="ftw-brand-page">

      {/* ── Brand Hero ── */}
      <section className="ftw-bp-hero">
        <div className="ftw-bp-hero__grid" />
        <div className="container ftw-bp-hero__inner">
          <button className="ftw-bp-back ftw-hero-animate ftw-hero-animate--1" onClick={() => navigate('/')}>
            <ArrowLeftIcon /> Back to FastTrack Wholesale
          </button>
          <div className="ftw-bp-hero__content ftw-hero-animate ftw-hero-animate--2">
            <div className="ftw-bp-hero__indicator">
          <img src={BRAND_LOGOS[brand.slug]} alt={brand.name} className="ftw-bp-hero__logo-img" />
        </div>
            <div>
              <div className="ftw-bp-hero__category">{brand.category}</div>
              <h1 className="ftw-bp-hero__name">{brand.name}</h1>
              <p className="ftw-bp-hero__tagline">{brand.tagline}</p>
              {brand.siteUrl && (
                <a href={brand.siteUrl} target="_blank" rel="noopener noreferrer" className="ftw-bp-hero__site-link">
                  Visit {brand.name} <ArrowRightIcon />
                </a>
              )}
            </div>
          </div>
          <div className="ftw-bp-hero__metrics ftw-hero-animate ftw-hero-animate--3">
            {brand.metrics.map((m, i) => (
              <div className="ftw-bp-hero__metric" key={i}>
                <span className="ftw-bp-hero__metric-value">{m.value}</span>
                <span className="ftw-bp-hero__metric-label">{m.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Hero image placeholder ── */}
      <section className="ftw-bp-hero-image ftw-animate-reveal">
        <div className="container">
          <PhotoPlaceholder aspectRatio="21/6" label="Brand Hero Image" className="ftw-bp-hero-img-placeholder" />
        </div>
      </section>

      {/* ── Brand Intro ── */}
      <section className="ftw-section ftw-section--light ftw-bp-intro">
        <div className="container ftw-bp-intro__inner ftw-animate-reveal">
          <SectionLabel>The FastTrack Story</SectionLabel>
          <h2 className="ftw-section-heading">The {brand.name} Partnership</h2>
          <p className="ftw-bp-intro__text">{brand.intro}</p>
        </div>
      </section>

      {/* ── Before / After ── */}
      <section className="ftw-section ftw-section--white ftw-bp-compare">
        <div className="container">
          <div className="ftw-bp-compare__grid">

            {/* Before */}
            <div className="ftw-bp-compare__col ftw-bp-compare__col--before ftw-animate-reveal">
              <div className="ftw-bp-compare__heading">
                <div className="ftw-bp-compare__dot ftw-bp-compare__dot--before" />
                <h3 className="ftw-bp-compare__title">{brand.beforeTitle}</h3>
              </div>
              <ul className="ftw-bp-list">
                {brand.beforePoints.map((pt, i) => (
                  <li className="ftw-bp-list__item ftw-bp-list__item--before" key={i}>
                    <span className="ftw-bp-list__icon ftw-bp-list__icon--before">—</span>
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* After */}
            <div className="ftw-bp-compare__col ftw-bp-compare__col--after ftw-animate-reveal" style={{ transitionDelay: '120ms' }}>
              <div className="ftw-bp-compare__heading">
                <div className="ftw-bp-compare__dot ftw-bp-compare__dot--after" />
                <h3 className="ftw-bp-compare__title">{brand.afterTitle}</h3>
              </div>
              <ul className="ftw-bp-list">
                {brand.afterPoints.map((pt, i) => (
                  <li className="ftw-bp-list__item ftw-bp-list__item--after" key={i}>
                    <span className="ftw-bp-list__icon ftw-bp-list__icon--after"><CheckIcon /></span>
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* ── Metrics Strip ── */}
      <section className="ftw-bp-metrics-strip">
        <div className="ftw-bp-metrics-strip__overlay" />
        <div className="container ftw-bp-metrics-strip__inner ftw-animate-reveal">
          {brand.metrics.map((m, i) => (
            <div className="ftw-bp-metrics-strip__metric" key={i}>
              <span className="ftw-bp-metrics-strip__value">{m.value}</span>
              <span className="ftw-bp-metrics-strip__label">{m.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── What We Did ── */}
      <section className="ftw-section ftw-section--light ftw-bp-actions">
        <div className="container">
          <div className="ftw-bp-actions__inner">
            <div className="ftw-bp-actions__left ftw-animate-reveal">
              <SectionLabel>Our Approach</SectionLabel>
              <h2 className="ftw-section-heading">What We Did</h2>
              <p className="ftw-section-lead">
                A structured, methodical approach to growing {brand.name}, from catalogue foundations through to multi-market distribution.
              </p>
            </div>
            <div className="ftw-bp-actions__right">
              {brand.whatWeDid.map((action, i) => (
                <div className="ftw-bp-action-item ftw-animate-reveal" key={i} style={{ transitionDelay: `${i * 60}ms` }}>
                  <div className="ftw-bp-action-item__num">{String(i + 1).padStart(2, '0')}</div>
                  <p className="ftw-bp-action-item__text">{action}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Product Photos ── */}
      <section className="ftw-section ftw-section--white ftw-bp-photos">
        <div className="container">
          <div className="ftw-bp-photos__header ftw-animate-reveal">
            <SectionLabel>Product Imagery</SectionLabel>
            <h2 className="ftw-section-heading">{brand.name} in Action</h2>
          </div>
          <div className="ftw-bp-photos__grid ftw-animate-reveal">
            <PhotoPlaceholder aspectRatio="1/1" label="Product Photo" className="ftw-bp-photos__main" />
            <div className="ftw-bp-photos__side">
              <PhotoPlaceholder aspectRatio="4/3" label="Product Photo" />
              <PhotoPlaceholder aspectRatio="4/3" label="Product Photo" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Brand CTA ── */}
      <section className="ftw-cta-section">
        <div className="ftw-cta-section__bg-pattern" />
        <div className="container ftw-bp-cta ftw-animate-reveal">
          <div>
            <div className="ftw-cta-section__label">Work With Us</div>
            <h2 className="ftw-cta-section__heading">Want Results Like These?</h2>
            <p className="ftw-cta-section__subtext">
              If you have a brand with growth potential and are looking for a committed wholesale distribution partner, we would welcome a conversation.
            </p>
            <div className="ftw-cta-section__actions">
              <a href="mailto:partnerships@fasttrackwholesale.co.uk" className="ftw-btn ftw-btn--amber ftw-btn--xl">
                Discuss a Partnership <ArrowRightIcon />
              </a>
              <button className="ftw-btn ftw-btn--outline-light" onClick={() => navigate('/')}>
                View All Brands
              </button>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

// ================================================================
// APP WITH ROUTER
// ================================================================
function App() {
  return (
    <BrowserRouter>
      <div className="ftw-app">
        <Navigation />
        <main>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/brands/:slug" element={<BrandPage />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </BrowserRouter>
  );
}

export default App;
